import { IDevicesPersistedState } from 'app/containers/rtc/DeviceManagerPersistence';
import { CONSTANT_USER_MEDIA_CONSTRAINTS } from 'app/services/rtc/constants';
import {
  AutoAction,
  DeviceState,
  DeviceType,
  IBehaviorOptions,
  InitialDeviceState,
  UserAction,
} from 'app/services/rtc/DeviceManager/DeviceState';
import WebAudio from 'app/services/WebAudio';
import DisplayMediaSource from 'app/services/rtc/DisplayMediaSource';

interface IDeps {
  readonly webAudio?: WebAudio;
  readonly settings?: IDevicesPersistedState;
}

export interface IDeviceManagerState {
  readonly audio: DeviceState;
  readonly video: DeviceState;
  readonly screen: DisplayMediaSource;
  readonly dismissed: boolean;
  readonly userAction: UserAction;
}

export type IDeviceManagerBehaviorOptions = {
  readonly [I in DeviceType]: IBehaviorOptions;
};

export default class DeviceManagerState implements IDeviceManagerState {
  /**
   * Entry point for creating DeviceManagerState.
   *
   * The dependencies aren't required on the constructor because we only
   * need them during initialization. However, the way we create new instances
   * instead of mutating existing ones means we would open the interface up
   * to misuse if we didn't require the dependencies. This method gives the
   * best of both words.
   */
  public static init({ webAudio, settings }: IDeps) {
    const audio = new InitialDeviceState({
      webAudio,
      type:                DeviceType.Audio,
      defaultSelector:     {},
      constantConstraints: CONSTANT_USER_MEDIA_CONSTRAINTS.audio,
      autoGain:            settings && settings.audio.autoGain,
      preferredDeviceId:   settings && settings.audio.preferredDeviceId,
    });

    const video = new InitialDeviceState({
      type:                DeviceType.Video,
      defaultSelector:     { facingMode: { ideal: 'user' } },
      constantConstraints: CONSTANT_USER_MEDIA_CONSTRAINTS.video,
      preferredDeviceId:   settings && settings.video.preferredDeviceId,
      mirrorPreview:       true,
    });

    const screen = new DisplayMediaSource();

    const skipAudio = settings && !settings.audio.active;
    const skipVideo = settings && !settings.video.active;

    return new DeviceManagerState({
      audio: skipAudio ? audio.skip() : audio,
      video: skipVideo ? video.skip() : video,
      screen,
      dismissed: false,
    });
  }

  public readonly audio: DeviceState;
  public readonly video: DeviceState;
  public readonly screen: DisplayMediaSource;
  public readonly dismissed: boolean;

  private constructor(
    prev: Omit<IDeviceManagerState, 'userAction'>,
    update?: Partial<IDeviceManagerState>,
  ) {
    this.audio     = prev.audio;
    this.video     = prev.video;
    this.screen    = prev.screen;
    this.dismissed = prev.dismissed;

    if (update) {
      Object.assign(this, update);
    }
  }

  public async start(options: IDeviceManagerBehaviorOptions) {
    // This is necessary so that both audio and video see labels as
    // empty if the access is not allowed. If we update video devices
    // after audio was approved, video devices will have labels on some
    // browsers (FF, Safari)
    let { audio, video } = await this.setBehaviorOptions(options).updateAvailableDevices();

    if (audio.isInitial) {
      audio = await audio.step();
    }

    if (this.video.isInitial && !audio.userAction) {
      // Run initial step on `video` if `audio` doesn't require user's
      // attention (which means it was approved auomatically or already
      // running)
      video = await video.step();
    }

    return new DeviceManagerState(this, { audio, video });
  }

  public async step(): Promise<DeviceManagerState> {
    let { audio, video } = this;

    if (audio.userAction) {
      audio = await audio.step();
    }

    // Check if `userAction` is still required on the audio device
    // and if it isn't, perform next step on the video device. This
    // is to facilitate autoconfiguration
    if (!audio.userAction && (video.userAction || video.isInitial)) {
      video = await video.step();
    }

    return new DeviceManagerState(this, { audio, video });
  }

  public stop() {
    return new DeviceManagerState(this, {
      audio: this.audio.stop(),
      video: this.video.stop(),
      screen: this.screen.stop(),
    });
  }

  public dismiss() {
    return new DeviceManagerState(this, { dismissed: true });
  }

  public reopen() {
    return new DeviceManagerState(this, { dismissed: false });
  }

  public async skipAudio() {
    return new DeviceManagerState(this, { audio: await this.audio.skip() });
  }

  public async skipVideo() {
    return new DeviceManagerState(this, { video: await this.video.skip() });
  }

  public async setAudioEnabled(enabled: boolean) {
    return new DeviceManagerState(this, { audio: await this.audio.setEnabled(enabled) });
  }

  public async toggleAudioEnabled() {
    return new DeviceManagerState(this, { audio: await this.audio.toggleEnabled() });
  }

  public async setVideoEnabled(enabled: boolean) {
    return new DeviceManagerState(this, { video: await this.video.setEnabled(enabled) });
  }

  public async toggleVideoEnabled() {
    return new DeviceManagerState(this, { video: await this.video.toggleEnabled() });
  }

  public async setPreferredAudioDeviceId(id: string) {
    return new DeviceManagerState(this, { audio: await this.audio.setPreferredDeviceId(id) });
  }

  public async setPreferredVideoDeviceId(id: string) {
    return new DeviceManagerState(this, { video: await this.video.setPreferredDeviceId(id) });
  }

  public async setAutoGain(autoGain: boolean) {
    return new DeviceManagerState(this, { audio: await this.audio.setAutoGain(autoGain) });
  }

  public async updateAvailableDevices() {
    return new DeviceManagerState(this, {
      audio: await this.audio.updateAvailableDevices(),
      video: await this.video.updateAvailableDevices(),
    });
  }

  public async updateTracksStates() {
    return new DeviceManagerState(this, {
      audio: await this.audio.updateTrackState(),
      video: await this.video.updateTrackState(),
      screen: this.screen.updateTrackState(),
    });
  }

  public get userAction(): UserAction {
    return this.audio.userAction || this.video.userAction;
  }

  public get autoAction(): AutoAction {
    const { audio, video } = this;
    // Make deciding `autoAction` dependent on `userAction` to avoid
    // cases where `autoAction` comes from `video` and `userAction`
    // comes from `audio`
    return audio.userAction ? audio.autoAction : video.autoAction;
  }

  public async toggleScreenSharing() {
    return new DeviceManagerState(this, {
      screen: await this.screen.toggle(),
    });
  }

  public async stopScreenSharing() {
    return new DeviceManagerState(this, {
      screen: this.screen.stop(),
    });
  }

  public dismissScreenSharingUserAction() {
    return new DeviceManagerState(this, {
      screen: this.screen.dismissUserAction(),
    });
  }

  private setBehaviorOptions(options: IDeviceManagerBehaviorOptions) {
    return new DeviceManagerState(this, {
      audio: this.audio.setBehaviorOptions(options[DeviceType.Audio]),
      video: this.video.setBehaviorOptions(options[DeviceType.Video]),
    });
  }
}
